import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Chip,
  Box,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { updateSupportTicketStatusAction } from "../../Actions/SupportTicketAction";
import { MyAttachFileIcon, MyCloseIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";

const { VALIDATION } = FRONTEND_MESSAGES;

// Constants
const STATUS_OPTIONS = [
  { value: "0", label: "Pending", emoji: "⚪", order: 0 },
  { value: "1", label: "In Progress", emoji: "🟡", order: 1 },
  { value: "3", label: "Completed", emoji: "🟢", order: 2 },
  { value: "2", label: "Rejected", emoji: "🔴", order: 3 },
  { value: "4", label: "On Hold", emoji: "🟣", order: 4 },
];

const STATUS_MAP = {
  0: { label: "Pending", emoji: "⚪" },
  1: { label: "In Progress", emoji: "🟡" },
  2: { label: "Rejected", emoji: "🔴" },
  3: { label: "Completed", emoji: "🟢" },
  4: { label: "On Hold", emoji: "🟣" },
};

const MIN_COMMENT_LENGTH = 10;
const MAX_COMMENT_LENGTH = 500;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
  'application/pdf', 'text/plain', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

// Validation schema - simplified to allow optional comment when attachments are present
const validationSchema = Yup.object({
  newStatus: Yup.string().required(VALIDATION.REQUIRED_FIELD("Status")).oneOf(["0", "1", "2", "3", "4"], VALIDATION.INVALID_FORMAT("Status")),
  statusComment: Yup.string()
    .trim()
    .min(MIN_COMMENT_LENGTH, VALIDATION.MIN_LENGTH("Status comment", MIN_COMMENT_LENGTH))
    .max(MAX_COMMENT_LENGTH, VALIDATION.MAX_LENGTH("Status comment", MAX_COMMENT_LENGTH)),
});

// Sub-components
const CurrentStatusDisplay = ({ status }) => {
  const statusInfo = STATUS_MAP[status] || STATUS_MAP[0];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        gutterBottom
        color="text.secondary"
      >
        Current Status 
      </Typography>
      <Chip
        label={`${statusInfo.emoji} ${statusInfo.label}`}
        variant="outlined"
        color="default"
        size="medium"
      />
    </Box>
  );
};

const StatusSelector = ({ formik }) => (
  <>
    <Typography variant="subtitle2" gutterBottom>New Status<span className="text-danger ms-1">*</span> </Typography>
    <TextField
      select
      fullWidth
      id="newStatus"
      name="newStatus"
      required
      value={formik.values.newStatus}
      onChange={(e) => {
        formik.handleChange(e);
        formik.setFieldTouched("newStatus", true, false);
      }}
      onBlur={formik.handleBlur}
      error={formik.touched.newStatus && Boolean(formik.errors.newStatus)}
      helperText={formik.touched.newStatus && formik.errors.newStatus}
      margin="normal"
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: { maxHeight: 300 },
          },
        },
      }}
    >
      {STATUS_OPTIONS.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
        >
          {option.emoji} {option.label}
        </MenuItem>
      ))}
    </TextField>
  </>
);

const CommentField = ({ formik, hasAttachments }) => {
  const currentLength = formik.values.statusComment.trim().length;
  const remainingForMin = MIN_COMMENT_LENGTH - currentLength;
  const remainingForMax = MAX_COMMENT_LENGTH - currentLength;
  const isMinReached = currentLength >= MIN_COMMENT_LENGTH;

  return (
    <Box className="mt-4">
      <Typography variant="subtitle2" gutterBottom>Status Update Note {!hasAttachments && <span className="text-danger ms-1">*</span>} </Typography>
      <TextField
        fullWidth
        multiline
        rows={4}
        id="statusComment"
        name="statusComment"
        required={!hasAttachments}
        placeholder="Explain the reason for this status change..."
        value={formik.values.statusComment}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        // error={formik.touched.statusComment && Boolean(formik.errors.statusComment)}
        helperText={formik.touched.statusComment && formik.errors.statusComment}
        inputProps={{
          maxLength: MAX_COMMENT_LENGTH,
        }}
      />
      
      <Box className="flex justify-start mt-1">
        {!isMinReached ? (
          <Typography 
            variant="caption" 
            sx={{ color: 'warning.main', fontWeight: 500 }}
          >
            {remainingForMin} more character{remainingForMin !== 1 ? 's' : ''} needed
          </Typography>
        ) : (
          <Typography 
            variant="caption" 
            className={remainingForMax < 50 ? 'warning.main' : 'text.secondary'}
          >
            {remainingForMax} character{remainingForMax !== 1 ? 's' : ''} remaining
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const AttachmentSection = ({ attachments, onAddAttachment, onRemoveAttachment, error }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
        return;
      }
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        alert(`File "${file.name}" type is not allowed.`);
        return;
      }
      onAddAttachment(file);
    });
    // Reset input
    event.target.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography variant="subtitle2" gutterBottom>Attachments </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<MyAttachFileIcon size={16} />}
          onClick={() => fileInputRef.current?.click()}
          disabled={attachments.length >= 5}
        >
          Add Files
        </Button>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx,.xls,.xlsx"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {attachments.length > 0 && (
        <List dense sx={{ bgcolor: 'grey.50', borderRadius: 1, mt: 1 }}>
          {attachments.map((file, index) =>{
            let fileName;
            if ((file.name).length <= 40){
                fileName = file.name
            }else{
                const ext = (file.name).slice((file.name).lastIndexOf("."));
                fileName = (file.name).slice(0, 40 - 4 - ext.length) + "...." + ext;
            };
            return (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={fileName}
                  secondary={formatFileSize(file.size)}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => onRemoveAttachment(index)}
                  >
                    <MyCloseIcon size={16} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            )})}
        </List>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Max 5 files, 10MB each. Supported: Images, PDF, DOC, XLS, TXT
      </Typography>
    </Box>
  );
};

// Main component
const StatusUpdateModal = ({ ticketData, currentStatus, open, onOpenChange, onStatusUpdated, refetchSupportTickets }) => {
  const [attachments, setAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState('');
  const formik = useFormik({
    initialValues: {
      newStatus: String(currentStatus || "0"),
      statusComment: "",
    },
    validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        // Custom validation: require either comment or attachments
        const hasComment = values.statusComment.trim().length >= 10;
        const hasAttachments = attachments.length > 0;

        if (!hasComment && !hasAttachments) {
          setErrors({
            statusComment: VALIDATION.AT_LEAST_ONE_REQUIRED(["Note (minimum 10 characters)", "Attachments"])
          });
          return;
        }

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('id', ticketData.id);
        formData.append('status', values.newStatus);
        formData.append('comment', values.statusComment.trim());

        // Add attachments to FormData
        attachments.forEach((file, index) => {
          formData.append(`attachments`, file);
        });

        const response = await updateSupportTicketStatusAction(formData, true); // true indicates multipart form data

        if (!response?.success) {
          throw new Error(response?.message || "Failed to update status");
        }

        onStatusUpdated?.();
        refetchSupportTickets?.();
        handleClose();
      } catch (error) {
        console.error("Status update failed:", error);
        setErrors({
          submit: error.message || "Failed to update status. Please try again.",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm({
        values: {
          newStatus: String(currentStatus || "0"),
          statusComment: "",
        },
      });
      setAttachments([]);
      setAttachmentError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentStatus]);

  // Clear comment error when attachments are added
  useEffect(() => {
    if (attachments.length > 0 && formik.errors.statusComment) {
      formik.setFieldError('statusComment', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments.length]);

  const handleClose = () => {
    formik.resetForm();
    setAttachments([]);
    setAttachmentError('');
    onOpenChange(false);
  };

  const handleAddAttachment = (file) => {
    if (attachments.length >= 5) {
      setAttachmentError('Maximum 5 files allowed');
      return;
    }

    setAttachments(prev => [...prev, file]);
    setAttachmentError('');
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setAttachmentError('');
  };

  const hasComment = formik.values.statusComment.trim().length >= 10;
  const hasAttachments = attachments.length > 0;
  const canSubmit = (hasComment || hasAttachments) && !formik.isSubmitting;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={formik.isSubmitting}
    >
      <DialogTitle>Update Ticket Status</DialogTitle>

      <DialogContent dividers>
        <CurrentStatusDisplay status={currentStatus} />

        {formik.errors.submit && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
          >
            {formik.errors.submit}
          </Alert>
        )}

        <StatusSelector formik={formik} />
        <CommentField formik={formik} hasAttachments={attachments.length > 0} />
        <AttachmentSection
          attachments={attachments}
          onAddAttachment={handleAddAttachment}
          onRemoveAttachment={handleRemoveAttachment}
          error={attachmentError}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={formik.isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={formik.handleSubmit}
          disabled={!canSubmit}
        >
          {formik.isSubmitting ? "Updating..." : "Update Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusUpdateModal;